/** Privacy policy - required for AdSense and general good practice.
 *  NOTE: CONTACT_EMAIL is a placeholder; set a real address before requesting AdSense review. */

export const CONTACT_EMAIL = 'contact@battle-standard.example'; // TODO: replace with a real address

const heading: React.CSSProperties = {
  fontFamily: "'Cinzel', Georgia, serif",
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--f-text-2)',
  margin: '28px 0 8px',
};

const text: React.CSSProperties = {
  fontFamily: "'Source Serif 4', Georgia, serif",
  fontSize: '14px',
  lineHeight: 1.7,
  color: 'var(--f-text-3)',
  margin: '0 0 12px',
};

const link: React.CSSProperties = { color: 'var(--f-primary)' };

export default function PrivacyPolicy() {
  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '32px 20px 64px' }}>
      <h1 style={{
        fontFamily: "'Cinzel', Georgia, serif",
        fontSize: '22px',
        fontWeight: 700,
        color: 'var(--f-text)',
        marginBottom: '4px',
      }}>
        Privacy Policy
      </h1>
      <p style={{ ...text, fontStyle: 'italic' }}>Last updated: 6 July 2026</p>

      <p style={text}>
        Battle Standard is a free, unofficial army builder and battle companion for Warhammer:
        The Old World. This page explains what data the site handles and where it goes. The short
        version: your army lists never leave your device, we run no accounts and collect nothing
        ourselves, and the only third parties involved are error monitoring, hosting, and
        advertising on content pages.
      </p>

      <h2 style={heading}>Your army lists and settings</h2>
      <p style={text}>
        Everything you build - army lists, unit selections, custom names, theme preferences - is
        stored locally in your browser's storage on your own device. It is never uploaded to us or
        anyone else. The export and import features move your data only when and where you choose.
        Clearing your browser's site data will delete your lists, so use the export feature to
        keep backups.
      </p>

      <h2 style={heading}>No accounts, no direct collection</h2>
      <p style={text}>
        The site has no sign-up, no login, and no forms that send us personal information. We do
        not collect, store or sell personal data ourselves.
      </p>

      <h2 style={heading}>Error monitoring (Sentry)</h2>
      <p style={text}>
        We use Sentry to capture crash reports and diagnose problems. When an error occurs, Sentry
        may receive technical information such as browser type, operating system, and what the app
        was doing at the time. Session replays are masked by default so text you type and content
        you view are not readable. See{' '}
        <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" style={link}>
          Sentry's privacy policy
        </a>{' '}
        for details.
      </p>

      <h2 style={heading}>Advertising (Google AdSense)</h2>
      <p style={text}>
        Content pages (such as the home page and rules reference) may display advertising served
        by Google AdSense. Google and its partners may use cookies or device identifiers to serve
        and measure ads; depending on your consent choices, ads may be personalised or
        non-personalised. The army builder and battle tracker screens do not show ads.
      </p>
      <p style={text}>
        You can learn how Google uses data from partner sites at{' '}
        <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" style={link}>
          policies.google.com/technologies/partner-sites
        </a>{' '}
        and manage ad personalisation at{' '}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" style={link}>
          adssettings.google.com
        </a>. Visitors in the UK/EEA are shown a consent message before any personalised
        advertising cookies are set.
      </p>

      <h2 style={heading}>Hosting (Cloudflare)</h2>
      <p style={text}>
        The site is served by Cloudflare Pages. Like any web host, Cloudflare processes IP
        addresses and request logs to deliver the site and protect it from abuse. See{' '}
        <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" style={link}>
          Cloudflare's privacy policy
        </a>.
      </p>

      <h2 style={heading}>Contact</h2>
      <p style={text}>
        Questions about this policy or your data:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} style={link}>{CONTACT_EMAIL}</a>.
      </p>
    </div>
  );
}
